import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection, getDocs, limit, query, where, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import type { Applicant, Profile } from '@scholarship-app/types';

interface ApplicantContextValue {
  /** All non-archived applicants in the current household. */
  applicants: Applicant[];
  /** The currently-selected applicant ID. */
  selectedApplicantId: string | null;
  /** The currently-selected applicant doc. */
  selectedApplicant: Applicant | null;
  /** The currently-selected applicant's profile. */
  profile: Profile | null;
  /** Whether the applicants list and selection are still loading. */
  loading: boolean;
  /** Whether the user can switch applicants. False for applicant-role users (locked to their own). */
  canSwitch: boolean;
  /** Switch the active applicant. */
  selectApplicant: (applicantId: string) => void;
}

const ApplicantContext = createContext<ApplicantContextValue | null>(null);

const STORAGE_KEY = 'scholarship_app__selected_applicant_id';

function loadStoredSelection(householdId: string): string | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.[householdId] ?? null;
  } catch {
    return null;
  }
}

function saveStoredSelection(householdId: string, applicantId: string | null) {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (applicantId) {
      parsed[householdId] = applicantId;
    } else {
      delete parsed[householdId];
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // ignore localStorage failures (private browsing, quota, etc.)
  }
}

export function ApplicantProvider({ children }: { children: ReactNode }) {
  const { membership } = useAuth();
  const [applicants, setApplicants] = useState<Applicant[]>([]);
  const [selectedApplicantId, setSelectedApplicantId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Applicant users are locked to their own profile; parent users can switch.
  const canSwitch = membership?.role === 'parent';

  // Load all applicants for the household.
  useEffect(() => {
    if (!db || !membership) {
      setApplicants([]);
      setLoading(false);
      return;
    }
    const ref = collection(db, 'households', membership.householdId, 'applicants');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.docs
          .map((d) => d.data() as Applicant)
          .filter((a) => !a.archived);
        setApplicants(list);
      },
      (err) => {
        console.error('[ApplicantContext] applicants snapshot failed:', err);
      }
    );
    return () => unsub();
  }, [membership]);

  // When applicants load OR membership changes, pick a default selection.
  useEffect(() => {
    if (!membership || applicants.length === 0) return;

    // Rule 1: Applicant-role users are forced to their own applicant_id.
    if (membership.role === 'applicant' && membership.applicantId) {
      setSelectedApplicantId(membership.applicantId);
      setLoading(false);
      return;
    }

    // Rule 2: Parent-role users — try persisted selection from localStorage.
    const stored = loadStoredSelection(membership.householdId);
    const storedValid = stored && applicants.some((a) => a.applicant_id === stored);
    if (storedValid) {
      setSelectedApplicantId(stored);
      setLoading(false);
      return;
    }

    // Rule 3: Default to the first applicant (typically Mia for the Mullinaxes).
    // Prefer one named Mia if present; otherwise the first one.
    const mia = applicants.find((a) => a.display_name?.includes('Mia'));
    const first = applicants[0];
    const picked = (mia ?? first)?.applicant_id ?? null;
    setSelectedApplicantId(picked);
    setLoading(false);
  }, [membership, applicants]);

  // Load the selected applicant's profile.
  useEffect(() => {
    if (!db || !membership || !selectedApplicantId) {
      setProfile(null);
      return;
    }
    const profileRef = doc(
      db,
      'households',
      membership.householdId,
      'applicants',
      selectedApplicantId,
      'profile',
      'profile'
    );
    const unsub = onSnapshot(
      profileRef,
      (snap) => {
        setProfile(snap.exists() ? (snap.data() as Profile) : null);
      },
      (err) => {
        console.error('[ApplicantContext] profile snapshot failed:', err);
      }
    );
    return () => unsub();
  }, [membership, selectedApplicantId]);

  function selectApplicant(applicantId: string) {
    if (!canSwitch || !membership) return;
    if (!applicants.some((a) => a.applicant_id === applicantId)) return;
    setSelectedApplicantId(applicantId);
    saveStoredSelection(membership.householdId, applicantId);
  }

  const selectedApplicant = applicants.find((a) => a.applicant_id === selectedApplicantId) ?? null;

  return (
    <ApplicantContext.Provider
      value={{
        applicants,
        selectedApplicantId,
        selectedApplicant,
        profile,
        loading,
        canSwitch,
        selectApplicant,
      }}
    >
      {children}
    </ApplicantContext.Provider>
  );
}

export function useApplicantContext() {
  const ctx = useContext(ApplicantContext);
  if (!ctx) throw new Error('useApplicantContext must be used within ApplicantProvider');
  return ctx;
}
